import { keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { PackageIcon, PlusIcon } from 'lucide-react';

import type { WarehouseCarrierServiceDTO } from '@invenco/warehousing-interface';

import { Button } from '@/components/core/button';
import { DialogTrigger } from '@/components/core/dialog';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
} from '@/components/data-table';
import { FormDialog } from '@/components/forms/form-dialog';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';

import { CreateWarehouseCarrierServiceDialogContent } from './create-warehouse-carrier-service-dialog-content';

const columnHelper = createColumnHelper<WarehouseCarrierServiceDTO>();

const columns = [
  columnHelper.accessor('carrierCode', { header: 'Carrier' }),
  columnHelper.accessor('carrierServiceCode', { header: 'Carrier service code' }),
  columnHelper.accessor('signatureRequired', {
    header: 'Signature required',
    cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
  }),
  columnHelper.accessor('incoTerms', { header: 'Inco terms' }),
  columnHelper.accessor('shippingMethodBookingAccountName', { header: 'Carrier account' }),
  columnHelper.accessor((row) => row.codes.find((code) => code.isPrimary)?.code ?? '', {
    id: 'primaryCode',
    header: 'Primary code',
  }),
  columnHelper.accessor('wmsInstanceCode', { header: 'WMS instance' }),
];

export function WarehouseCarrierServiceList() {
  const { warehousingGateway } = useGateways();
  const onClickRow = useRowNavigationCallback<WarehouseCarrierServiceDTO>(
    '/warehouse-carrier-services',
  );

  const { state, filters, onPaginationChange, onSortingChange } = useDataTableState({ columns });

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'warehouseCarrierServices',
    input: { search: filters.search },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        warehousingGateway.getWarehouseCarrierServices(
          { search: input.search, cursor, take },
          { signal },
        ),
      ),
    placeholderData: keepPreviousData,
  });

  const table = useDataTable({
    data,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    manualPagination: false,
    manualSorting: false,
    autoResetPageIndex: false,
    onPaginationChange,
    onSortingChange,
    state,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <PackageIcon />
        </HeaderIcon>
        <HeaderTitle>Warehouse carrier services</HeaderTitle>
        <HeaderActions>
          <FormDialog>
            <DialogTrigger asChild>
              <Button>
                <PlusIcon />
                Create
              </Button>
            </DialogTrigger>
            <CreateWarehouseCarrierServiceDialogContent />
          </FormDialog>
        </HeaderActions>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
